import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { refreshTheme } from './store/slices/themeSlice';
import { trackPageView } from './utils/analytics';
import { canAccessFeature } from './utils/tierAccess';
import PWAUpdateToast from './components/shared/PWAUpdateToast';
import InstallPrompt from './components/shared/InstallPrompt';

// Pages — eagerly loaded (landing + auth flow)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import DashboardLayout from './components/layouts/DashboardLayout';
import ContentPage from './pages/ContentPage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import UIStylePreview from './pages/UIStylePreview';

// Lazy-loaded pages (heavy chart/table dependencies)
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const VideoAnalyticsPage = lazy(() => import('./pages/VideoAnalyticsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const HealthMonitor = lazy(() => import('./pages/HealthMonitor'));
const RevenuePage = lazy(() => import('./pages/RevenuePage'));
const DealsPage = lazy(() => import('./pages/DealsPage'));
const IntelligenceHub = lazy(() => import('./pages/IntelligenceHub'));
const IdeaStudio = lazy(() => import('./pages/IdeaStudio'));
const WeeklyReport = lazy(() => import('./pages/WeeklyReport'));
const WebAnalyticsDashboard = lazy(() => import('./pages/WebAnalyticsDashboard'));
const VideoEditorPage = lazy(() => import('./pages/VideoEditorPage'));
const EditorProjectsList = lazy(() => import('./components/editor/EditorProjectsList'));

// Loading fallback for lazy routes — branded pulsing symbol
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <img
      src="/OmniConnX_Web_Brand_Package/icons/omniconnx-v2-symbol-96x96.png"
      alt="Loading"
      className="w-12 h-12 animate-pulse"
    />
  </div>
);

// Track page views on every route change
const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Tier-gated Route — redirects to settings billing tab if user lacks access
// Admin users bypass all tier restrictions
const TierRoute = ({ featureKey, children }) => {
  const { user } = useSelector((state) => state.auth);
  const userTier = user?.tier || 'FREE';
  const isAdmin = user?.role === 'ADMIN' || user?.roles?.some((r) => r.name === 'ADMIN' || r === 'ADMIN');

  if (!canAccessFeature(userTier, featureKey, isAdmin)) {
    return <Navigate to="/dashboard/settings?tab=billing&reason=upgrade_required" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);

  useEffect(() => {
    // Apply the resolved theme on load, then re-check every minute so an "auto" (time-of-day)
    // preference flips between light and dark at 6am / 6pm without needing a page reload.
    dispatch(refreshTheme());
    const id = setInterval(() => dispatch(refreshTheme()), 60_000);
    return () => clearInterval(id);
  }, [dispatch]);

  return (
    <>
      <Router>
        <PageViewTracker />
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy/data-deletion" element={<DataDeletionPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/ui-preview" element={<UIStylePreview />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AnalyticsDashboard />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="health" element={<TierRoute featureKey="health"><HealthMonitor /></TierRoute>} />
              <Route path="content" element={<ContentPage />} />
              <Route path="revenue" element={<TierRoute featureKey="revenue"><RevenuePage /></TierRoute>} />
              <Route path="deals" element={<TierRoute featureKey="deals"><DealsPage /></TierRoute>} />
              {/* Gated server-side by the creator_intelligence flag; the nav link is hidden unless enabled. */}
              <Route path="intelligence" element={<IntelligenceHub />} />
              <Route path="intelligence/weekly" element={<WeeklyReport />} />
              <Route path="idea-studio" element={<IdeaStudio />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="video-analytics" element={<TierRoute featureKey="video-analytics"><VideoAnalyticsPage /></TierRoute>} />
              {/* Alias for the renamed "Content Analytics" surface — keeps the old /video-analytics link working. */}
              <Route path="content-analytics" element={<TierRoute featureKey="video-analytics"><VideoAnalyticsPage /></TierRoute>} />
              <Route path="web-analytics" element={<WebAnalyticsDashboard />} />
              <Route path="editor" element={<EditorProjectsList />} />
            </Route>

            {/* Video Editor — full-screen, no dashboard chrome */}
            <Route
              path="/dashboard/editor/:projectId"
              element={<ProtectedRoute><VideoEditorPage /></ProtectedRoute>}
            />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Router>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: mode === 'dark' ? '#1e293b' : '#ffffff',
            color: mode === 'dark' ? '#f1f5f9' : '#0f172a',
            border: mode === 'dark' ? '1px solid #334155' : '1px solid #e2e8f0',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />

      {/* PWA: service worker update toast + install banner */}
      <PWAUpdateToast />
      <InstallPrompt />
    </>
  );
}

export default App;
